import React from "react";
import { Link } from "react-router-dom";
import { useContent } from "@/hooks/useContent";
import PageSEO from "@/components/PageSEO";

const IMG = "/assets/about-mission-landscape.jpg";

const timeline = [
  { year: "Vision", title: "The Calling", body: "The founder is called to Colombia. A vision arrives: a sanctuary that is both ceremony and infrastructure." },
  { year: "Practice", title: "Rebirthing Genesis", body: "The modality is codified — breathwork, nervous system regulation, and integration practice." },
  { year: "Land", title: "The Ground Held", body: "The first parcel is stewarded. The cloud forest becomes home." },
  { year: "Community", title: "Tribes of Humanity", body: "The Global Alliance forms — healers, teachers, artists, builders across continents." },
  { year: "Institution", title: "Departments Named", body: "The sanctuary reorganizes as an institution — pillars, departments, guilds, and fellowships." },
  { year: "Operating System", title: "NEOS v1", body: "The digital nervous system goes live — one platform for retreats, community, and the movement." },
  { year: "Expansion", title: "The Next Nodes", body: "Culture, School, and the Institute for Regenerative Civilization enter development." },
];

export default function About() {
  const { t } = useContent("about");
  return (
    <div>
      <PageSEO
        title={"About | Celestial Seed Sanctuary"}
        description={"Learn how Celestial Seed Sanctuary is building a long-term institution integrating arts, ecology, education, wellness, and community stewardship in Colombia."}
      />
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-16">
          <div className="overline mb-6" data-testid="about-hero-overline">{t("hero.overline", "The Institution")}</div>
          <h1 className="font-serif text-5xl lg:text-7xl tracking-tight leading-[1.05] max-w-4xl" data-testid="about-title">
            {t("hero.title", "A sanctuary is an agreement between people, land, and time — held with reverence, and built with care.")}
          </h1>
          <p className="text-ink-soft text-xl mt-8 max-w-3xl" data-testid="about-hero-lead">
            {t("hero.lead", "Celestial Seed is organized as a living institution — five pillars, ten working departments, one operating system. What began as a sanctuary is becoming the digital and physical headquarters of a global regenerative alliance.")}
          </p>
          <div className="mt-8">
            <Link to="/charter" data-testid="about-charter-link" className="pill pill-outline">Read the Institutional Charter →</Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 border-y border-line grid md:grid-cols-5 gap-8 bg-surface">
        {[
          { n: "01", t: "Healing" },
          { n: "02", t: "Education" },
          { n: "03", t: "Ecology" },
          { n: "04", t: "Culture" },
          { n: "05", t: "Community" },
        ].map(p => (
          <div key={p.n}>
            <div className="font-serif text-3xl text-terracotta">{p.n}</div>
            <div className="font-serif text-2xl mt-2">{p.t}</div>
            <div className="overline mt-1">Pillar</div>
          </div>
        ))}
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 py-12">
        <img src={IMG} alt="Colombian farmland" className="w-full rounded-3xl aspect-[4/5] object-cover" />
        <div className="space-y-8">
          <div>
            <div className="overline mb-3" data-testid="about-mission-overline">{t("mission.overline", "Mission")}</div>
            <p className="text-xl text-ink leading-relaxed font-serif" data-testid="about-mission-body">
              {t("mission.body", "To seed and steward regenerative culture — through ceremony, education, healing, architecture, and honest work with the land.")}
            </p>
          </div>
          <div>
            <div className="overline mb-3" data-testid="about-vision-overline">{t("vision.overline", "Vision")}</div>
            <p className="text-ink-soft leading-relaxed" data-testid="about-vision-body">
              {t("vision.body", "A world in which sanctuaries, ecovillages, and schools are woven into a global fabric — where any person can find their tribe, their teacher, their calling, and a piece of land to belong to.")}
            </p>
          </div>
          <div>
            <div className="overline mb-3">Values</div>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-ink">
              {["Reverence", "Regeneration", "Reciprocity", "Rigour", "Rest", "Rooted Action"].map(v => (
                <li key={v} className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-terracotta" />{v}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section id="tribes-of-humanity" className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="rounded-3xl border border-line bg-surface p-8 lg:p-12 grid lg:grid-cols-[1fr_auto] gap-8 items-center">
          <div>
            <div className="overline mb-3">Global Alliance</div>
            <h2 className="font-serif text-4xl lg:text-5xl">Tribes of Humanity Global Alliance</h2>
            <p className="text-ink-soft mt-4 max-w-3xl leading-relaxed">Celestial Seed Sanctuary is home to the Tribes of Humanity Global Alliance, connecting the Sanctuary's work with a wider network of people, practice and cultural exchange.</p>
          </div>
          <a href="https://ecovillage.org/map/community/celestial-seed-sanctuary/" target="_blank" rel="noopener noreferrer" className="pill pill-outline">Find Celestial Seed on GEN →</a>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="overline mb-3" data-testid="about-departments-overline">{t("departments.overline", "Departments")}</div>
        <h2 className="font-serif text-4xl lg:text-5xl mb-3" data-testid="about-departments-headline">
          {t("departments.headline", "The working wings of the institution.")}
        </h2>
        <p className="text-ink-soft max-w-2xl mb-12" data-testid="about-departments-lead">
          {t("departments.lead", "Each department has its own leadership, budget, workflow, and long-term arc. All operate through the shared infrastructure of NEOS.")}
        </p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
          {[
            "The Sanctuary — Land, food forest, watershed, natural building",
            "Rebirthing Genesis — Breathwork, nervous system, healing",
            "Dubbi EcoVillage — Housing, architecture, master planning",
            "School of Practical Wisdom & Life — Education, apprenticeships, guilds",
            "Culture & Arts — Residencies, festivals, cultural exchange, indigenous knowledge, publishing",
            "Tribes of Humanity Global Alliance — International network",
            "Healer Fellowship — Scholarships & leadership",
            "Volunteer & Operator Corps — Stewardship, training, residency",
            "Grant & Impact Center — Foundations, sponsors, campaigns",
            "Media & Publishing Studio — Podcast, books, film, photography",
            "Institute for Regenerative Civilization — Research & publications (coming)",
            "Archives & Legacy — Institutional memory, oral histories, decision archive (coming)",
            "Observatory, Learning & Evaluation — Impact measurement, story collection, ecological observation, annual reporting (coming)",
            "NEOS — The nervous system connecting everything",
          ].map((d) => (
            <div key={d} className="border-t border-line pt-4 text-ink text-sm leading-relaxed">{d}</div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="overline mb-3" data-testid="about-evolution-overline">{t("evolution.overline", "Evolution")}</div>
        <h2 className="font-serif text-4xl lg:text-5xl mb-12" data-testid="about-evolution-headline">
          {t("evolution.headline", "A slow, deliberate unfolding.")}
        </h2>
        <div className="grid gap-8">
          {timeline.map((t) => (
            <div key={t.year} className="grid grid-cols-[140px_1fr] gap-8 border-t border-line pt-8">
              <div className="font-serif text-2xl text-terracotta leading-tight">{t.year}</div>
              <div>
                <div className="font-serif text-2xl text-ink">{t.title}</div>
                <p className="text-ink-soft mt-2 max-w-2xl">{t.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
